import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Checkbox,
    CircularProgress,
    IconButton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Delete } from '@mui/icons-material';
import to from 'await-to-js';
import { Alert, AutoGrid, Button, DialogV2, SelectField, TextAreaField } from 'c-components';
import DateRangeField from 'c-components/Forms/DateRangeField';
import apiClient from 'c-data/apiClient';
import { useAPIClientRequest } from 'c-hooks';
import {
    InsertionOrderAttribute as Attribute,
    InsertionOrderSearchType as SearchType,
} from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import type { DspTargeting } from './useDspTargeting';
import type { TabDraft } from './lib';
import { buildRulePayloads, defaultInsertionOrderDateRange } from './lib';
import { formatRuleDateRange, ruleIdentifierLabel, sortRulesByMatches } from './rulesTable';

type Props = {
    campaignId: number;
    open: boolean;
    onClose: () => void;
    dateStart?: string | null;
    dateEnd?: string | null;
    targeting: DspTargeting;
};

const ANY = 'any';

const TABS: { attribute: Attribute; label: string }[] = [
    {
        attribute: Attribute.insertionOrderId,
        label: 'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.tabInsertionOrderId',
    },
    {
        attribute: Attribute.insertionOrderName,
        label: 'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.tabInsertionOrderName',
    },
    {
        attribute: Attribute.advertiser,
        label: 'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.tabAdvertiser',
    },
];

const SEARCH_TYPES = [
    { label: 'Contains', value: SearchType.contains },
    { label: 'Exact', value: SearchType.exact },
    { label: 'Starts with', value: SearchType.startsWith },
    { label: 'Ends with', value: SearchType.endsWith },
];

const emptyDrafts = (): TabDraft[] =>
    TABS.map(({ attribute }) => ({
        attribute,
        // An insertion order id can only be matched exactly, so its tab starts -- and stays -- exact.
        searchType:
            attribute === Attribute.insertionOrderId ? SearchType.exact : SearchType.contains,
        text: '',
    }));

/**
 * UL-7912 -- Edit Table Parameters: the same modal it is for media, with one addition.
 *
 * **Lines within a tab are alternatives; filled tabs are combined with AND.** Type "T-Mobile" in the
 * advertiser tab and "National Park" in the name tab, press Apply once, and that is one rule: an
 * insertion order must match both. The exact rules -- including why two multi-line tabs are refused
 * rather than cross-multiplied -- live in `buildRulePayloads`, and the preview line reads from the
 * same function, so what it says is what Apply does.
 */
const InsertionOrderRulesDialog: React.FC<Props> = ({
    campaignId,
    open,
    onClose,
    dateStart,
    dateEnd,
    targeting,
}) => {
    const t = useCommonTranslation();
    const defaultRange = useMemo(
        () => defaultInsertionOrderDateRange(dateStart, dateEnd),
        [dateStart, dateEnd],
    );

    const [tab, setTab] = useState<Attribute>(Attribute.insertionOrderName);
    const [drafts, setDrafts] = useState<TabDraft[]>(emptyDrafts);
    const [customer, setCustomer] = useState<string>(ANY);
    const [range, setRange] = useState<[string, string] | null>(defaultRange);
    const [customerOptions, setCustomerOptions] = useState<string[]>([]);
    const [selectedRuleIds, setSelectedRuleIds] = useState<Set<number>>(new Set());
    const [skippedMessages, setSkippedMessages] = useState<string[]>([]);

    const catalogue = useAPIClientRequest(apiClient.Entities.Campaign.insertionOrderCatalogue);
    const create = useAPIClientRequest(apiClient.Entities.Campaign.createInsertionOrderRules);
    const remove = useAPIClientRequest(apiClient.Entities.Campaign.deleteInsertionOrderRules);

    const { start: fetchCatalogue } = catalogue;

    useEffect(() => {
        if (!open) return;
        setDrafts(emptyDrafts());
        setRange(defaultRange);
        setCustomer(ANY);
        setSelectedRuleIds(new Set());
        setSkippedMessages([]);

        // One tiny request, for the DSPs the catalogue actually holds.
        (async () => {
            const [err, res] = await to(fetchCatalogue({ perPage: 1, showFilters: true }));
            const options = res?.data?.meta?.filters?.find(
                f => f.key === 'filter.customer',
            )?.options;
            if (!err && options?.length) setCustomerOptions(options.map(o => String(o.value)));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const result = useMemo(
        () =>
            buildRulePayloads({
                tabs: drafts,
                customer,
                dateFrom: range?.[0] ?? null,
                dateTo: range?.[1] ?? null,
            }),
        [customer, drafts, range],
    );

    // `in` rather than `!result.ok`: this repo compiles without strictNullChecks, where narrowing a
    // union on a boolean literal does not take.
    const errorCode = 'error' in result ? result.error.code : null;
    const rulesToCreate = useMemo(() => ('rules' in result ? result.rules : []), [result]);
    const nothingEntered = errorCode === 'nothing_entered';

    const updateDraft = useCallback((attribute: Attribute, change: Partial<TabDraft>) => {
        setDrafts(current =>
            current.map(d => (d.attribute === attribute ? { ...d, ...change } : d)),
        );
    }, []);

    const { start: startCreate } = create;
    const apply = useCallback(async () => {
        if (rulesToCreate.length === 0) return;

        const [err, res] = await to(startCreate(campaignId, rulesToCreate));
        if (err) return;

        const skipped = res?.data?.data?.skipped ?? [];
        setSkippedMessages(skipped.map(s => s.message));

        // Keep what was typed when something was skipped, so the user can see which line it was.
        if (skipped.length === 0) setDrafts(emptyDrafts());

        await targeting.refresh();
    }, [campaignId, rulesToCreate, startCreate, targeting]);

    const { start: startRemove } = remove;
    const deleteRules = useCallback(
        async (ruleIds: number[]) => {
            if (ruleIds.length === 0) return;
            const [err] = await to(startRemove(campaignId, ruleIds));
            if (err) return;
            setSelectedRuleIds(new Set());
            await targeting.refresh();
        },
        [campaignId, startRemove, targeting],
    );

    const rules = useMemo(
        // Most-matching first, as the media list is: the rules doing the work lead.
        () => sortRulesByMatches(targeting.rules),
        [targeting.rules],
    );

    const allSelected = rules.length > 0 && selectedRuleIds.size === rules.length;

    return (
        <DialogV2
            open={open}
            onClose={onClose}
            maxWidth="xl"
            title="Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.title"
        >
            <TabContext value={tab}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={(_, value) => setTab(value)}>
                        {TABS.map(({ attribute, label }) => {
                            const filled =
                                (drafts.find(d => d.attribute === attribute)?.text ?? '').trim() !==
                                '';
                            return (
                                <Tab
                                    key={attribute}
                                    value={attribute}
                                    label={`${t(label)}${filled ? ' •' : ''}`}
                                />
                            );
                        })}
                    </TabList>
                </Box>

                <AutoGrid xs={12} md={4} gap={2} pt={2}>
                    <SelectField
                        label={t('Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.customer')}
                        options={[
                            {
                                label: t('Modules.Main.Campaigns.CampaignSchedule.dsp.filters.any'),
                                value: ANY,
                            },
                            ...customerOptions.map(c => ({ label: c, value: c })),
                        ]}
                        value={customer}
                        size="small"
                        onChange={e => setCustomer(String(e.target.value))}
                    />
                    <DateRangeField
                        value={range ?? [null, null]}
                        onChange={value =>
                            setRange(
                                value?.[0] && value?.[1]
                                    ? [String(value[0]), String(value[1])]
                                    : null,
                            )
                        }
                        label={t(
                            'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.dateRange',
                        )}
                        inputProps={{ size: 'small' }}
                    />
                </AutoGrid>

                {TABS.map(({ attribute }) => {
                    const draft = drafts.find(d => d.attribute === attribute);
                    const isId = attribute === Attribute.insertionOrderId;

                    return (
                        <TabPanel key={attribute} value={attribute} sx={{ px: 0 }}>
                            <Stack gap={2}>
                                <Box maxWidth={260}>
                                    <SelectField
                                        label={t(
                                            'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.searchType',
                                        )}
                                        options={
                                            isId
                                                ? SEARCH_TYPES.filter(
                                                      o => o.value === SearchType.exact,
                                                  )
                                                : SEARCH_TYPES
                                        }
                                        value={draft.searchType}
                                        disabled={isId}
                                        size="small"
                                        onChange={e =>
                                            updateDraft(attribute, {
                                                searchType: e.target.value as SearchType,
                                            })
                                        }
                                    />
                                </Box>
                                <TextAreaField
                                    label={t(
                                        'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.identifiers',
                                    )}
                                    helperText={t(
                                        'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.identifiersHelper',
                                    )}
                                    rows={5}
                                    value={draft.text}
                                    onChange={e => updateDraft(attribute, { text: e.target.value })}
                                />
                            </Stack>
                        </TabPanel>
                    );
                })}
            </TabContext>

            <Stack
                direction="row"
                gap={2}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
            >
                <Box>
                    {rulesToCreate.length > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {t('Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.preview', {
                                count: rulesToCreate.length,
                            })}
                        </Typography>
                    )}
                    {errorCode !== null && !nothingEntered && (
                        <Alert severity="warning">
                            {t(
                                `Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.errors.${errorCode}`,
                            )}
                        </Alert>
                    )}
                </Box>
                <Button onClick={apply} disabled={rulesToCreate.length === 0 || create.isLoading}>
                    {create.isLoading ? (
                        <CircularProgress size={20} />
                    ) : (
                        t('Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.apply')
                    )}
                </Button>
            </Stack>

            {create.error && <Alert severity="error">{String(create.error)}</Alert>}
            {skippedMessages.length > 0 && (
                <Alert severity="info" onClose={() => setSkippedMessages([])}>
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.skipped', {
                        messages: skippedMessages.join(' '),
                    })}
                </Alert>
            )}

            {rules.length === 0 ? (
                <Typography variant="body2" color="text.secondary" py={2}>
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.noneSaved')}
                </Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        checked={allSelected}
                                        indeterminate={selectedRuleIds.size > 0 && !allSelected}
                                        onChange={() =>
                                            setSelectedRuleIds(
                                                allSelected
                                                    ? new Set()
                                                    : new Set(rules.map(r => r.id)),
                                            )
                                        }
                                    />
                                </TableCell>
                                <TableCell>
                                    {t(
                                        'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.columns.identifier',
                                    )}
                                </TableCell>
                                <TableCell>
                                    {t(
                                        'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.columns.dateRange',
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {t(
                                        'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.columns.count',
                                    )}
                                </TableCell>
                                <TableCell>
                                    {t(
                                        'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.columns.customer',
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {t(
                                        'Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.columns.actions',
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rules.map(rule => (
                                <TableRow key={rule.id}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            checked={selectedRuleIds.has(rule.id)}
                                            onChange={() =>
                                                setSelectedRuleIds(current => {
                                                    const next = new Set(current);
                                                    if (next.has(rule.id)) next.delete(rule.id);
                                                    else next.add(rule.id);
                                                    return next;
                                                })
                                            }
                                        />
                                    </TableCell>
                                    {/* The whole rule, conditions joined by AND -- including the id for a pick. */}
                                    <TableCell>{ruleIdentifierLabel(rule)}</TableCell>
                                    <TableCell>{formatRuleDateRange(rule)}</TableCell>
                                    {/* Zero is shown, not hidden: a rule matching nothing yet stays saved and will match. */}
                                    <TableCell align="right">
                                        {rule.insertion_order_count}
                                    </TableCell>
                                    <TableCell>
                                        {rule.customer ??
                                            t(
                                                'Modules.Main.Campaigns.CampaignSchedule.dsp.filters.any',
                                            )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => deleteRules([rule.id])}
                                            disabled={remove.isLoading}
                                        >
                                            <Delete fontSize="inherit" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}

            {remove.error && <Alert severity="error">{String(remove.error)}</Alert>}
            <Stack direction="row" justifyContent="flex-end">
                <Button
                    color="error"
                    variant="outlined"
                    disabled={selectedRuleIds.size === 0 || remove.isLoading}
                    onClick={() => deleteRules([...selectedRuleIds])}
                >
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.parameters.deleteSelected')}
                </Button>
            </Stack>
        </DialogV2>
    );
};

export default InsertionOrderRulesDialog;
